import React, { useEffect, useState } from 'react';
import { matchPath, Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import qs from 'query-string';
import className from 'classnames';

// Scenes
import Error404 from '../scenes/Error404';

// Components
import Layout from '../components/Layout';
import DisneyCosmicToy from '../components/DisneyCosmicToy';
import MothersDayFlowers from '../components/MothersDayFlowers';
import ChildrenDayRainbow from '../components/ChildrenDayRainbow';
import IdleCodeWidget from '../scenes/Home/partial/IdleCodeWidget';

// State
import { updateLocation } from '../state/modules/location';
import { initApplePay } from '../state/modules/payTypes/actions';
import { fetchCartItems } from '../state/modules/cart/actions';
import { getLocalStorageCheckout } from '../state/modules/order/actions';
import { fetchFooterLinks } from '../state/modules/ui/actions';
import { fetchRestaurants } from '../state/modules/restaurant/actions';
import { setAllowedNumberBonuses } from '../state/modules/sailplay/actions';
import { getUnconditionalList } from '../state/modules/stock/actions';
import { fetchAddresses } from '../state/modules/address/actions';

// Router
import { getAllRoutes } from './routes';

// Utils
import useMediaQueryMatch from '../utils/useMediaQueryMatch';
import {
  CITY_THEME_DISNEY,
  CITY_THEME_MOTHERS_DAY,
  CITY_THEME_CHILDRENS_DAY,
  getActiveCityTheme,
  getCityThemeConfig,
  getThemeBodyClassNames,
  getVisualActiveCityTheme,
  syncBodyCityTheme,
} from '../utils/cityTheme';

// Styles
import '../styles/reset.styl';
import '../styles/main.styl';
import { fetchProfile } from '../state/modules/user/actions';

const default404 = {
  layout: {
    simple: false,
    theme: 'secondary',
  },
};
const findCurrentRoute = (routes, pathname) => {
  return routes.find(route => matchPath(pathname, { path: route.path, exact: true })) || default404;
};

export default function App() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const isRealPhone = useSelector(state => state.responsive.isRealPhone);
  const { isPhone, isTablet } = useSelector(state => state.responsive);
  const { userCity } = useSelector(state => state?.city);
  const { title, help_number, image, _supertag } = userCity || {};
  const activeCityTheme = getActiveCityTheme(userCity);
  const visualCityTheme = getVisualActiveCityTheme(userCity);
  const cityThemeConfig = getCityThemeConfig(visualCityTheme);
  // если убрать отсюда isAuthenticated, то при выходе разлогина нас не будет редиректить со страницы профиля
  // так как не будет ререндера
  const isAuthenticated = useSelector(state => state.user.isAuthenticated);
  const allRoutes = getAllRoutes();
  const [currentRoute, setCurrentRoute] = useState(default404);
  useEffect(() => {
    if (isAuthenticated) dispatch(fetchProfile());
  }, [isAuthenticated]);
  useEffect(() => {
    setCurrentRoute(findCurrentRoute(allRoutes, location.pathname));
  }, [location]);

  useEffect(() => {
    // запрашиваем акции и условия к ним (rewards: Получите скидку 25% на заказ, condition: Необходимо сделать заказ самовывозом)
    dispatch(getUnconditionalList());
    // Проверка возможности использования дополнительных форм оплаты
    dispatch(initApplePay());
    // достаем из локалстоража данные с чекаута
    dispatch(getLocalStorageCheckout());
    // запрашиваем линки для футера
    dispatch(fetchFooterLinks());
    // запрашиваем список всех ресторанов
    dispatch(fetchRestaurants());
  }, []);

  useEffect(() => {
    const routeForScroll = findCurrentRoute(allRoutes, location.pathname);

    // возвращаем скролл наверх при переходе между страницами
    if (!routeForScroll.layout.isScrollToTopDisabled) {
      window.scrollTo(0, 0);
    }

    // для аналитики
    dispatch(updateLocation(location));
  }, [allRoutes, dispatch, location]);

  // перезапрашиваем корзину при входе/выходе
  useEffect(() => {
    // для активации промокода через url /?code=20WOW
    const { code } = qs.parse(location.search);
    dispatch(fetchCartItems(code));

    // убираем из урла code
    if (code) {
      history.replace({ search: qs.exclude(location.search, ['code']) });
    }

    // запрашиваем список сохраненных адресов если авторизованы
    if (isAuthenticated) {
      dispatch(fetchAddresses());
    }
  }, [isAuthenticated]);

  useMediaQueryMatch();

  useEffect(() => {
    syncBodyCityTheme(activeCityTheme);
  }, [activeCityTheme]);

  const isAppReady = allRoutes.length > 0 && userCity?._supertag;
  return (
    <>
      <Helmet>
        <html lang={intl.locale} />
        <body
          className={className({
            desktop: currentRoute.newest && !isRealPhone,
            ...getThemeBodyClassNames(activeCityTheme),
          })}
          data-city-theme={visualCityTheme || undefined}
        />
        <title>{title}</title>
        <meta charSet="utf-8" />
        <meta name="application-name" content="Papa John's" />
        <meta name="description" content={`${title} | ${intl.formatMessage({ id: 'og.title' })}`} />
        <meta name="og:title" content={`${title} | ${intl.formatMessage({ id: 'og.title' })}`} />
        <meta name="og:description" content={intl.formatMessage({ id: 'og.description' })} />
        <meta name="og:image" content={image} />
        <meta name="og:phone_number" content={help_number} />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        {isRealPhone ? (
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, viewport-fit=cover, maximum-scale=1, user-scalable=no"
          />
        ) : (
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, viewport-fit=cover"
          />
        )}
        <meta name="yandex-verification" content="eb38f08d0d6d04cd" />
        <meta
          name="google-site-verification"
          content="V0LbsmTPVXVlbaxKgNEX-7NwRQytU2WNiMI5WKWwEqs"
        />
        <meta name="theme-color" content={cityThemeConfig?.themeColor ?? '#ffffff'} />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicon2023/favicon2023.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon2023/favicon2023.png" />
        <link rel="manifest" href="/site.webmanifest" />
      </Helmet>
      <AppEffects />
      {visualCityTheme === CITY_THEME_DISNEY && !isPhone && !isTablet && <DisneyCosmicToy />}
      {activeCityTheme === CITY_THEME_MOTHERS_DAY && !isPhone && !isTablet && <MothersDayFlowers />}
      {activeCityTheme === CITY_THEME_CHILDRENS_DAY && !isPhone && !isTablet && <ChildrenDayRainbow />}
      <Layout {...currentRoute.layout}>
        {isAppReady ? (
          <Switch>
            {allRoutes.map(route => (
              <Route key={route.path} path={route.path} exact={route.exact ?? true}>
                {route.redirect
                  ? routeProps => {
                      if (routeProps.staticContext) {
                        routeProps.staticContext.statusCode = 301;
                      }
                      return (
                        <Redirect to={{ ...routeProps.location, pathname: `/${_supertag}` }} />
                      );
                    }
                  : route.children}
              </Route>
            ))}
            <Route path="*">
              <Error404 />
            </Route>
          </Switch>
        ) : null}

        {!isAuthenticated && <IdleCodeWidget />}
      </Layout>
    </>
  );
}

// вынес в отдельный компонент, чтобы основной App не ререндерился при изменениях корзины
function AppEffects() {
  const dispatch = useDispatch();
  const cart = useSelector(state => state.cart);

  // если в корзине изменилась итоговая цена или туда попали подарки за баллы
  // то надо пересчитать колво введенного кешбека
  useEffect(() => {
    if (cart.sailplay_discount !== undefined) {
      dispatch(setAllowedNumberBonuses());
    }
  }, [cart]);

  return null;
}
